import React, { useState, useEffect, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import { useLanguage } from '../../contexts/LanguageContext';
import { chartOfAccountsService, ChartOfAccountSummaryDto } from '../../services/chartOfAccounts.service';

interface AccountSelectorProps {
  value: number | null;
  onChange: (accountId: number | null) => void;
  label?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  onlyFinalAccounts?: boolean;
  parentAccountId?: number | null;
}

const AccountSelector: React.FC<AccountSelectorProps> = ({
  value,
  onChange,
  label,
  error = false,
  helperText,
  disabled = false,
  required = false,
  fullWidth = true,
  onlyFinalAccounts = false,
  parentAccountId = null
}) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const [accounts, setAccounts] = useState<ChartOfAccountSummaryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<ChartOfAccountSummaryDto | null>(null);

  const loadAccounts = useCallback(async () => {
    try {
      setLoading(true);
      
      if (parentAccountId) {
        const data = await chartOfAccountsService.getByParentId(parentAccountId);
        const filteredData = onlyFinalAccounts 
          ? data.filter((a: ChartOfAccountSummaryDto) => a.isFinalAccount)
          : data;
        setAccounts(filteredData);
      } else {
        const data = onlyFinalAccounts 
          ? await chartOfAccountsService.getFinalAccounts()
          : await chartOfAccountsService.getFlatList();
        setAccounts(data);
      }
    } catch (err) {
      console.error('Failed to load accounts:', err);
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, [onlyFinalAccounts, parentAccountId]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    if (value && accounts.length > 0) {
      const account = accounts.find(a => a.accountId === value);
      setSelectedAccount(account || null);
    } else {
      setSelectedAccount(null);
    }
  }, [value, accounts]);

  const handleChange = (_event: any, newValue: ChartOfAccountSummaryDto | null) => {
    setSelectedAccount(newValue);
    onChange(newValue?.accountId || null);
  };

  return (
    <Autocomplete
      value={selectedAccount}
      onChange={handleChange}
      options={accounts}
      getOptionLabel={(option) =>
        `${option.accountCode} - ${isRTL ? option.accountNameAr : option.accountName}`
      }
      loading={loading}
      disabled={disabled}
      fullWidth={fullWidth}
      autoHighlight
      openOnFocus
      isOptionEqualToValue={(option, value) => option.accountId === value.accountId}
      slotProps={{
        popper: { sx: { minWidth: 450 } },
        paper: { sx: { maxHeight: 350 } },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label || (isRTL ? 'الحساب' : 'Account')}
          placeholder={isRTL ? 'ابحث بالكود أو الاسم...' : 'Search by code or name...'}
          error={error}
          helperText={helperText}
          required={required}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...otherProps } = props as any;
        return (
          <Box
            key={`account-${option.accountId}-${option.accountCode}`}
            component="li"
            {...otherProps}
            sx={{
              display: 'flex',
              gap: 1.5,
              alignItems: 'center',
              py: 1.5,
              px: 2,
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                fontWeight: 'bold',
                color: 'primary.main',
                minWidth: '100px',
                fontSize: '0.9rem'
              }}
            >
              {option.accountCode}
            </Typography>
            <Typography variant="body2" sx={{ flex: 1 }}>
              {isRTL ? option.accountNameAr : option.accountName}
            </Typography>
          </Box>
        );
      }}
      filterOptions={(options, { inputValue }) => {
        if (!inputValue) return options;

        const searchTerm = inputValue.toLowerCase().trim();
        return options.filter(option =>
          option.accountCode.toLowerCase().includes(searchTerm) ||
          option.accountName.toLowerCase().includes(searchTerm) ||
          (option.accountNameAr && option.accountNameAr.includes(inputValue))
        );
      }}
      noOptionsText={isRTL ? 'لا توجد حسابات مطابقة' : 'No matching accounts'}
      loadingText={isRTL ? 'جاري التحميل...' : 'Loading...'}
    />
  );
};

export default AccountSelector;